import { makeVar, useReactiveVar } from '@apollo/client';
import { useEffect } from 'react';

import type { DemandGridRowData } from '../demand-grid/types';

export type UpdateDemandGridRowDataPayload = Partial<DemandGridRowData> & { id: string };

export type UpdateDemandGridRowsReactiveVarPayload = {
  fullRefreshRequired: boolean;
  /**
   * Rows to selectively fetch from the server and update in the grid.
   * This is useful when the data change is significant and requires refetching the data from the server to ensure data consistency.
   */
  fetchIds?: string[];
  /**
   * Rows to update directly using the payload.
   * This is useful for small data changes
   */
  updated?: UpdateDemandGridRowDataPayload[];
  /**
   * Rows ids to remove from the grid
   */
  removedIds?: string[];
};

/**
 * Reactive var to trigger demand grid row data transaction right after user performs demand grid data change.
 */
export const updateDemandGridRowsReactiveVar =
  makeVar<Partial<UpdateDemandGridRowsReactiveVarPayload> | null>(null);

type Props = Readonly<{
  onChange: (changeSet: UpdateDemandGridRowsReactiveVarPayload) => void;
}>;

/**
 * Listen to updateDemandGridRowsReactiveVar and call onChange when it changes.
 * This hook should be used as reactive var consumer only.
 */
export const useUpdateDemandGridRowsReactiveVar = ({ onChange }: Props) => {
  const value = useReactiveVar(updateDemandGridRowsReactiveVar);

  useEffect(() => {
    if (value) {
      onChange({ fullRefreshRequired: false, ...value });
      updateDemandGridRowsReactiveVar(null);
    }
  }, [value, onChange]);
};
